/**
 * How a tentacle moves — one description, evaluated by both the mesh and the gun.
 *
 * Adam: "the tentacles themselves are stiff and 'wagging' but not moving. There should be
 * some organic movement in them so that they seem alive."
 *
 * The limb was already being deformed, with the wrong motion. Three things made it read as
 * a post rather than muscle, and all three are fixed here:
 *
 *  1. **It was twenty times too slow.** The old idle sway ran at 0.34 rad/s — one complete
 *     sway every eighteen seconds, with the lift and the fore-aft terms slower still. At
 *     play speed that is not movement, it is drift, and the eye reads drift as a stiff
 *     object on a current.
 *  2. **The whole limb bent as one arc.** The old wave number was 5.2 radians across the
 *     entire length: 0.83 of a single wavelength. One arc rocking back and forth is the
 *     definition of wagging. A slither needs two or three crests alive on the limb at once
 *     so the S is visibly *travelling* up it.
 *  3. **The wave ran backwards.** `sin(ωT + k·s)` carries crests from the tip toward the
 *     root. A contraction starts at the base and runs outward — which is what the Kraken's
 *     own eight arms have always done (`simTime * speed - t * travel` in
 *     render/ships/krakenMotion.ts). The standalone limb never got that treatment.
 *
 * This module lives in `content/` rather than beside the mesh builder because the
 * simulation has to agree with it. The hit volume is displaced by exactly these offsets at
 * exactly the same simulation time, so a limb is shootable where it is drawn rather than
 * where it was authored — the same contract the wave field has with the ocean shader.
 *
 * Pure and deterministic: simulation time and entity id in, metres out. No wall clock, so
 * captures and replays stay comparable.
 */

export interface TentacleMotion {
  /** Radians per second of the primary contraction. ~1.2 = a four-second stroke. */
  speed: number;
  /** Radians of phase across the whole limb — how many crests live on it at once. */
  travel: number;
  /** Peak lateral excursion at the tip, metres. */
  sway: number;
  /** Peak vertical excursion, metres. */
  heave: number;
  /** Extra reach carried by the last fifth of the limb, metres. */
  tipCurl: number;
  /** Which way the primary stroke swings, radians in the limb's own frame. */
  direction: number;
  /** Radians per second of roll about the limb's own axis. */
  rollSpeed: number;
  /** Peak axial roll, radians. Turns the sucker rows into and out of view. */
  roll: number;
  phase: number;
}

/**
 * Eight limbs that do not move alike.
 *
 * The old animation's only per-limb variation was a phase offset, so six tentacles were
 * one prop shown six times. These differ in stroke speed, in how many crests they carry,
 * in how far they throw their tip, in which plane they swing through and in how hard they
 * roll — one whips while its neighbour turns over slowly.
 */
const LIMB_MOTION: readonly TentacleMotion[] = [
  { speed: 1.34, travel: 8.6, sway: 15.5, heave: 4.6, tipCurl: 8.0, direction: 0.24, rollSpeed: 0.71, roll: 0.52, phase: 0.7 },
  { speed: 1.02, travel: 10.4, sway: 12.2, heave: 5.8, tipCurl: 10.4, direction: 1.31, rollSpeed: 0.48, roll: 0.68, phase: 2.4 },
  { speed: 1.71, travel: 7.5, sway: 18.4, heave: 3.4, tipCurl: 6.2, direction: -0.62, rollSpeed: 0.94, roll: 0.37, phase: 4.1 },
  { speed: 1.18, travel: 9.8, sway: 13.8, heave: 6.1, tipCurl: 11.0, direction: 2.18, rollSpeed: 0.57, roll: 0.61, phase: 1.3 },
  { speed: 1.52, travel: 8.1, sway: 16.9, heave: 4.0, tipCurl: 7.1, direction: 0.87, rollSpeed: 0.82, roll: 0.44, phase: 5.6 },
  { speed: 0.94, travel: 11.2, sway: 11.4, heave: 6.6, tipCurl: 9.6, direction: -1.44, rollSpeed: 0.43, roll: 0.74, phase: 3.0 },
  { speed: 1.44, travel: 9.1, sway: 17.2, heave: 3.8, tipCurl: 8.8, direction: 2.74, rollSpeed: 0.88, roll: 0.49, phase: 0.2 },
  { speed: 1.09, travel: 10.0, sway: 12.9, heave: 5.2, tipCurl: 10.1, direction: -2.31, rollSpeed: 0.52, roll: 0.66, phase: 4.8 },
];

/**
 * The furthest any limb in the table throws a point off the authored curve, metres.
 *
 * The broad-phase reject around a limb has to be grown by this, or a round aimed at where
 * the limb has swung to would be discarded before the narrow phase ever looked.
 */
export const TENTACLE_MAX_EXCURSION = LIMB_MOTION.reduce(
  (worst, m) => Math.max(worst, Math.hypot(m.sway * 1.3, m.heave + m.tipCurl)),
  0,
);

/**
 * The motion this particular limb has.
 *
 * Two limbs that land on the same table row are pulled apart by a phase derived from the
 * id, so a spawn wave of six is never two of anything moving in lockstep.
 */
export function tentacleMotionFor(id: number): TentacleMotion {
  const slot = ((id % LIMB_MOTION.length) + LIMB_MOTION.length) % LIMB_MOTION.length;
  const base = LIMB_MOTION[slot] as TentacleMotion;
  const scatter = (Math.sin(id * 78.233) * 0.5 + 0.5) * Math.PI * 2;
  return { ...base, phase: base.phase + scatter };
}

export interface TentacleOffset {
  x: number;
  y: number;
  z: number;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Seconds a limb takes to come up out of the sea. */
export const TENTACLE_RISE_SECONDS = 1.5;

/**
 * How far out of the water this limb has come: 0 still under, 1 fully extended.
 *
 * The limb used to arrive at full height on the frame it spawned — Adam: "they just
 * appeared out of the blue, they did [not] appear from below." Easing out fast and
 * settling slow gives the root a shove and lets the hook come over last.
 */
export function tentacleEmergence(ageSeconds: number): number {
  if (!(ageSeconds < TENTACLE_RISE_SECONDS)) return 1;
  const t = clamp01(ageSeconds / TENTACLE_RISE_SECONDS);
  return 1 - Math.pow(1 - t, 2.4);
}

/**
 * The travelling contraction, in metres, at progress `t` along the limb.
 *
 * `hold` is how strongly the limb is gripping something (0 idle, 1 fully wrapped): a limb
 * that has seized a hull stops writhing freely and holds, so the idle motion is dialled
 * back rather than fighting the grip.
 */
export function tentacleOffset(
  m: TentacleMotion,
  t: number,
  simTime: number,
  hold = 0,
): TentacleOffset {
  const s = clamp01(t);
  // Planted root, free tip: the base is anchored in something we never see.
  const free = Math.pow(s, 1.3);
  const idle = 1 - clamp01(hold) * 0.84;
  if (free === 0 || idle === 0) return { x: 0, y: 0, z: 0 };

  // Minus `s * travel`: the crest is born at the root and runs outward.
  const phase = simTime * m.speed - s * m.travel + m.phase;
  const primary = Math.sin(phase);
  // A faster, smaller ripple on a deliberately non-integer ratio, so the two never come
  // back into step and the limb does not repeat a clean sine.
  const ripple = Math.sin(simTime * m.speed * 2.37 - s * m.travel * 1.83 + m.phase * 2.1);
  const curl = Math.pow(s, 2.6);

  const along = (primary * m.sway + ripple * m.sway * 0.26) * free * idle;
  // The cross term is what stops the tip tracing a straight line: it writes a slow figure
  // instead, which is the difference between a limb searching and a metronome.
  const cross = ripple * m.sway * 0.34 * free * idle;
  const lift = (
    Math.sin(phase * 0.79 + 1.1) * m.heave * free
    + Math.sin(simTime * m.speed * 0.53 + m.phase) * m.tipCurl * curl
  ) * idle;

  const dx = Math.cos(m.direction);
  const dz = Math.sin(m.direction);
  return {
    x: dx * along - dz * cross,
    y: lift,
    z: dz * along + dx * cross,
  };
}

/**
 * Roll about the limb's own axis, radians.
 *
 * The cheapest strong signal that something is alive. The mesh already carries two rows of
 * suckers down the inside of the hook; turning them into and out of view as the wave
 * passes reads as muscle twisting, and it costs one rotation of an existing local frame.
 * It never moves the centreline, so the hit volume does not care.
 */
export function tentacleRoll(m: TentacleMotion, t: number, simTime: number): number {
  const s = clamp01(t);
  return Math.sin(simTime * m.rollSpeed - s * m.travel * 0.55 + m.phase * 0.7)
    * m.roll * Math.pow(s, 1.15);
}
